import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fadeUp, Eyebrow, VIEWPORT, VIEWPORT_SM, staggerContainer } from "./primitives";
import { capture } from "@/lib/analytics";
import { EVENTS } from "@/lib/analyticsEvents";
import { useLanguage } from "@/i18n/LanguageContext";

const TEST_IDS = ["audience-buyer", "audience-owner", "audience-dealer"];

// Chaque profil part vers ce qui le concerne, pas vers l'inscription par
// défaut : l'acheteur n'a besoin d'aucun compte pour consulter.
const HREFS = ["/verifier", null, "/signataires"];

import { STUDIO_URL } from "@/lib/studio";

// RDEC-023 : l'outil de travail des ateliers, un autre produit Provena.
export { STUDIO_URL };

/**
 * Trois portes d'entrée, une par profil.
 *
 * RDEC-023 : un atelier est renvoyé vers Provena Studio, son outil de
 * travail ; Registry reste la couche de provenance publique.
 *
 * Reprend les trois personas que la section « Le constat » décrivait par
 * leur angoisse (« Et si c'était un faux ? »). Les décrire deux fois ne
 * servait à rien ; leur donner chacun le lien qui les concerne, si. La
 * destination change par profil : consulter pour l'acheteur, enregistrer
 * pour le propriétaire, le programme signataires pour le professionnel.
 */
export const AudienceSection = ({ registerTo }) => {
  const { t } = useLanguage();
  const items = t("audience.items");
  if (!Array.isArray(items)) return null;

  const targets = HREFS.map((href) => href || registerTo);

  return (
    <section
      id="pour-qui"
      data-testid="audience-section"
      className="relative scroll-mt-28 py-16 md:py-32 border-t border-ink/5 bg-surface-muted overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={fadeUp}
          className="max-w-2xl mb-12 md:mb-16"
        >
          <Eyebrow>{t("audience.eyebrow")}</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mt-4">
            {t("audience.title")}
          </h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT_SM}
          variants={staggerContainer}
          className="grid gap-px bg-ink/5 sm:grid-cols-3"
        >
          {items.map((item, i) => (
            <motion.div key={TEST_IDS[i]} variants={fadeUp}>
              <Link
                to={targets[i]}
                onClick={
                  HREFS[i] === "/signataires"
                    ? () => capture(EVENTS.SIGNATORY_CLICKED, { source: "audience_section" })
                    : undefined
                }
                data-testid={TEST_IDS[i]}
                className="group flex h-full flex-col bg-surface-muted p-8 md:p-10 transition-colors duration-500 hover:bg-surface-raised"
              >
                <span className="mb-6 block font-serif text-sm tracking-[0.3em] text-gold-ink">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-xl md:text-2xl mb-4">{item.role}</h3>
                <p className="flex-1 text-sm md:text-base text-ink-muted leading-relaxed">
                  {item.body}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-gold-ink">
                  {item.cta}
                  <ArrowRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <p
          data-testid="audience-studio"
          className="mt-10 text-sm md:text-base text-ink-muted"
        >
          {t("audience.studio.question")}{" "}
          <a
            href={STUDIO_URL}
            rel="noopener"
            className="text-gold-ink underline-offset-4 hover:underline"
          >
            {t("audience.studio.link")}
          </a>
        </p>
      </div>
    </section>
  );
};

export default AudienceSection;
